import axios from 'axios'
import { i18nObject } from 'boot/i18n'
import { useBettingStore } from 'src/stores/betting'
import { useUserAuthStore } from 'src/stores/user/auth'
import { AUTOLOGIN_ENDPOINTS } from 'src/common/autologin/constants'
import { CONFIG } from 'src/common/config'
import { tokenService } from 'src/common/token-service'

const axiosOptions = {
  baseURL: CONFIG.API.API_BK_URL,
  timeout: CONFIG.API.TIMEOUT,
  headers: {
    Vary: 'Accept-Encoding',
  },
}

const api = axios.create(axiosOptions)
let errorCounter = 0
const MAX_ERRORS = 10

const SKIP_LOGOUT_ON_401 = [
  '/authservice/api/v1/user/signin/',
  AUTOLOGIN_ENDPOINTS.appToken,
  AUTOLOGIN_ENDPOINTS.validate,
]

export default () => {
  api.interceptors.request.use(
    async (config) => {
      const tokenFromCookies = tokenService.getToken()
      const tokenFromStore = useUserAuthStore().getToken

      const token = tokenFromStore || tokenFromCookies

      if (token) {
        config.headers.Authorization = `JWT ${token}`
      }

      if (window.__E2E__) {
        config.headers['x-e2e-test'] = 1
      }

      if (i18nObject?.global?.locale?.value) {
        config.headers['Accept-Language'] = i18nObject?.global?.locale?.value
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    },
  )

  api.interceptors.response.use(
    (response) => {
      if (!response || response.status !== 200) {
        console.error('API error: ', response)
        return Promise.reject({ status: response?.status, data: null, error: true })
      }

      errorCounter = 0
      return response
    },
    async (error) => {
      console.error('API error: ', error)

      const originalRequest = error.config
      if (!originalRequest) {
        return Promise.reject(error)
      }

      if (originalRequest?.url === 'token-refresh/' || originalRequest._retry) {
        return Promise.reject(error)
      }

      const tokenIsExpired =
        (error.response?.status === 401 && !SKIP_LOGOUT_ON_401.includes(error.config.url)) ||
        (error.response?.data?.non_field_errors &&
          error.response?.data?.non_field_errors[0] === 'Signature has expired.')

      if (tokenIsExpired) {
        localStorage.removeItem('token')

        const ENDPOINTS_USER = [/signin\//]

        if (ENDPOINTS_USER.some((rx) => rx.test(originalRequest?.url))) {
          return Promise.reject(error)
        }

        originalRequest._retry = true
        delete originalRequest.headers.Authorization
        return api(originalRequest)
      }

      errorCounter++

      if (errorCounter >= MAX_ERRORS) {
        useBettingStore().updateSnackbarMessage(i18nObject.global.t('TooManyFailedRequestsDataMayBeOutdated'))
      }

      return Promise.reject({
        status: error.response?.status,
        data: error.response?.data,
        error: true,
        err_desc: error.response?.data?.err_desc || error.message,
      })
    },
  )
}

export { api }
